/**
 * Live autocomplete for a searchbar.
 *
 * Markup contract: a positioned wrapper carrying js_searchbar_autocomplete and
 * data-autocomplete-mode around a searchbar plus an empty results container
 * carrying js_searchbar_autocomplete_results.
 *
 * Kaufen injects the card_html the endpoint renders, because the buying card needs
 * product data the autocomplete rows don't carry; the other modes build their own.
 */
import {
  createSearchResultCard,
  SEARCH_DEBOUNCE_MS,
  SEARCH_FORM_TYPE_REPAIR,
  SEARCH_FORM_TYPE_SELL,
  SEARCH_MIN_CHARS,
} from './search-result-card';

const MODE_BUY = 'buy';
const MAX_RESULTS = 5;

const ENDPOINT_PARAMS = {
  [SEARCH_FORM_TYPE_SELL]: 'is_ankauf=1',
  [SEARCH_FORM_TYPE_REPAIR]: 'is_repair=1',
  [MODE_BUY]: 'big_design_tiles=1&card=search_result',
};

function buildEndpointUrl(mode, query) {
  return '/findTypes.php?' + ENDPOINT_PARAMS[mode] + '&query=' + encodeURIComponent(query);
}

function renderResults(container, mode, results) {
  container.innerHTML = '';
  let shown = 0;
  results.forEach(function (item) {
    if (shown >= MAX_RESULTS) {
      return;
    }
    if (mode === MODE_BUY) {
      if (!item.card_html) {
        return;
      }
      container.insertAdjacentHTML('beforeend', item.card_html);
    } else {
      container.appendChild(createSearchResultCard(item, mode));
    }
    shown++;
  });
  container.hidden = shown === 0;
}

function bindInstance(wrapper) {
  const input = wrapper.querySelector('.js_searchbar_input');
  const resultsContainer = wrapper.querySelector('.js_searchbar_autocomplete_results');
  const mode = wrapper.dataset.autocompleteMode;
  if (!input || !resultsContainer || !ENDPOINT_PARAMS[mode]) {
    return null;
  }

  let debounceTimer = null;
  let abortController = null;

  function close() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    resultsContainer.innerHTML = '';
    resultsContainer.hidden = true;
  }

  input.addEventListener('input', function () {
    const query = input.value.trim();
    if (query.length < SEARCH_MIN_CHARS) {
      close();
      return;
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(function () {
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();
      fetch(buildEndpointUrl(mode, query), { signal: abortController.signal })
        .then(function (resp) {
          if (!resp.ok) {
            throw new Error('HTTP ' + resp.status);
          }
          return resp.json();
        })
        .then(function (data) {
          renderResults(resultsContainer, mode, data.suggestions || []);
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') {
            // The previous query's cards no longer answer the current input.
            close();
            console.error(err);
          }
        });
    }, SEARCH_DEBOUNCE_MS);
  });

  input.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      input.value = '';
      close();
    }
  });

  const closeBtn = wrapper.querySelector('.js_searchbar_close');
  if (closeBtn) {
    closeBtn.addEventListener('click', close);
  }

  return { wrapper: wrapper, close: close };
}

function initSearchbarAutocomplete() {
  const instances = [];

  document.querySelectorAll('.js_searchbar_autocomplete').forEach(function (wrapper) {
    const instance = bindInstance(wrapper);
    if (instance) {
      instances.push(instance);
    }
  });

  if (instances.length === 0) {
    return;
  }

  document.addEventListener('click', function (e) {
    instances.forEach(function (instance) {
      if (!instance.wrapper.contains(e.target)) {
        instance.close();
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', initSearchbarAutocomplete);
